import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./date-picker-input.css";
import { Calendar } from "lucide-react";
import { parse, format } from "date-fns";
import { enGB } from "date-fns/locale"; // day-first locale

interface DatePickerInputProps {
  value?: string; // supports "dd/MM/yyyy" or "yyyy-MM-dd"
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  maxDate?: Date;
  className?: string;
  scrollContainerSelector?: string;
  disabled?: boolean;
}

const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value = "",
  onChange,
  error = "",
  placeholder = "Select date",
  maxDate,
  scrollContainerSelector,
  className = "",
  disabled = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const datepickerRef = useRef<DatePicker>(null);

  // Parse incoming `value` safely with an explicit format
  useEffect(() => {
    if (!value) {
      setSelectedDate(null);
      return;
    }
    let parsed: Date | null = null;

    // Support both dd/MM/yyyy and ISO yyyy-MM-dd from parent
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      parsed = parse(value, "dd/MM/yyyy", new Date());
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      parsed = parse(value, "yyyy-MM-dd", new Date());
    } else {
      // last-resort: try Date constructor (not recommended)
      const d = new Date(value);
      parsed = isNaN(d.getTime()) ? null : d;
    }

    setSelectedDate(parsed);
  }, [value]);

  // Close on outer scroll (unchanged)
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    const scrollTarget = scrollContainerSelector
      ? document.querySelector(scrollContainerSelector)
      : window;

    scrollTarget?.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollTarget?.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, scrollContainerSelector]);

  return (
    <div className={`relative w-full`}>
      {/* Click-through overlay to focus the input */}
      {!disabled && (
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={() => datepickerRef.current?.setFocus()}
        />
      )}

      <div className="relative">
        <DatePicker
          ref={datepickerRef}
          selected={selectedDate}
          onChange={(date: Date | null) => {
            if (disabled) {
              return;
            }
            setSelectedDate(date);
            // 🔒 Always emit dd/MM/yyyy to parent
            onChange(date ? format(date, "dd/MM/yyyy") : "");
            setIsOpen(false);
          }}
          onCalendarOpen={() => !disabled && setIsOpen(true)}
          onCalendarClose={() => setIsOpen(false)}
          open={isOpen && !disabled}
          onClickOutside={() => setIsOpen(false)}
          dateFormat="dd/MM/yyyy"
          placeholderText={placeholder}
          maxDate={maxDate}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          portalId="root"
          locale={enGB} // ✅ day-first calendar behavior
          onKeyDown={e => e.preventDefault()}
          disabled={disabled}
          className={`relative z-0 w-full rounded-xl border ${className} ${
            error ? "border-red-500" : "border-slate-200"
          } ${
            disabled ? "cursor-not-allowed bg-slate-50 opacity-50" : "bg-white"
          } px-10 py-2.5 font-sans text-[14px] font-medium text-slate-700 outline-none transition-all duration-200 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-[#66A0CD] focus:ring-1 focus:ring-[#66A0CD]/20`}
        />

        <Calendar
          size={18}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
      </div>

      {error && (
        <p className="mt-1 font-sans text-sm font-semibold text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

export default DatePickerInput;
